/**
 * eCare Design System tokens — canonical TS mirror of
 * `docs/ecare-styleguide.md` v1.1 and `src/styles/ecare-design-system.css`.
 *
 * Values here are the light theme. Anything that must flip with the dark theme
 * should read the CSS custom property via `cssVar()` rather than these literals.
 */

export const ECARE_FONT = {
  serif:
    '"Source Serif 4", "Source Serif Pro", "Tiempos Text", "Charter", "Iowan Old Style", Georgia, serif',
  sans: '"Inter", "Styrene B", "Söhne", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
} as const;

export const ECARE_FONT_WEIGHT = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

/** Global slight tightening applied to body text. */
export const ECARE_LETTER_SPACING = {
  body: "-0.003em",
  display: "-0.02em",
  title: "-0.01em",
  cardTitle: "-0.005em",
  overline: "0.06em",
} as const;

export const ECARE_COLOR = {
  accent: {
    primary: "#0E4486",
    hover: "#0C386F",
    pressed: "#0A2F5C",
    soft: "#E4ECF5",
    softBorder: "#C6D6E8",
    softText: "#2B4D76",
  },
  bg: {
    base: "#F7F9FC",
    surface: "#FFFFFF",
    surfaceRaised: "#FFFFFF",
    sidebar: "#E7ECF8",
    subtle: "#EEF2FB",
    hover: "#ECF0F9",
    selected: "#DFE6F5",
    input: "#FFFFFF",
    code: "#EFF3FB",
    overlay: "rgba(30, 40, 60, 0.32)",
  },
  text: {
    primary: "#1C2330",
    secondary: "#46505F",
    tertiary: "#69727F",
    disabled: "#AEB6C2",
    onAccent: "#FFFFFF",
    link: "#0E4486",
    selection: "rgba(14, 68, 134, 0.25)",
  },
  border: {
    default: "#E2E7F1",
    strong: "#CCD5E4",
    divider: "#EAEEF6",
    focus: "#0E4486",
    focusRing: "rgba(14, 68, 134, 0.35)",
  },
  success: { main: "#3F8F5C", bg: "#E6F1EA" },
  warning: { main: "#C77A2C", bg: "#FAEED9" },
  error: { main: "#B33A2C", bg: "#F7E0DC" },
  info: { main: "#0E4486", bg: "#E1EAF5" },
} as const;

/** Brand blue leads, then warm multi-hue. Max one accent series per chart. */
export const ECARE_CHART_COLORS = [
  "#0E4486",
  "#C96442",
  "#3F8F5C",
  "#A66B3A",
  "#7B5BA6",
  "#C77A2C",
  "#5C8C8A",
  "#B33A6B",
] as const;

export const ECARE_CHART_AXIS = { grid: "#EAEEF6", axis: "#8A93A4" } as const;

export const ECARE_RADIUS = {
  xs: 6,
  sm: 9,
  md: 12,
  lg: 18,
  xl: 24,
  "2xl": 30,
  full: 9999,
  /** Shared by buttons, inputs and dropdowns. */
  control: 14,
} as const;

export const ECARE_SHADOW = {
  xs: "0 1px 2px rgb(30 40 60 / 0.04)",
  sm: "0 2px 6px rgb(30 40 60 / 0.06)",
  md: "0 4px 12px rgb(30 40 60 / 0.08), 0 2px 4px rgb(30 40 60 / 0.04)",
  lg: "0 12px 28px rgb(30 40 60 / 0.12), 0 4px 8px rgb(30 40 60 / 0.06)",
  xl: "0 24px 48px rgb(30 40 60 / 0.18), 0 8px 16px rgb(30 40 60 / 0.08)",
} as const;

export const ECARE_LAYOUT = {
  headerHeight: 44,
  pageHeaderPadding: "24px 32px 20px",
  pageBodyPadding: "24px 32px 32px",
  filterBarPadding: "12px 32px",
  cardPadding: 20,
  gridGap: 16,
} as const;

export const ECARE_Z_INDEX = {
  kebabMenu: 50,
  drawerOverlay: 600,
  pageLoader: 700,
  drawer: 800,
  topBar: 900,
  toast: 900,
  modal: 1000,
  appSwitcherPopover: 1000,
  editHint: 1100,
} as const;

export const ECARE_EASING = {
  standard: "cubic-bezier(0.2, 0, 0, 1)",
  rise: "cubic-bezier(0.22, 1, 0.36, 1)",
} as const;

/** Nothing bounces, nothing runs longer than 240ms. */
export const ECARE_DURATION = {
  micro: 100,
  hover: 120,
  progress: 200,
  modal: 180,
  toast: 220,
  drawer: 240,
  reveal: 240,
} as const;

export const ECARE_CONTROL_HEIGHT = {
  sm: 28,
  md: 36,
  lg: 44,
  input: 40,
  inputSm: 32,
} as const;

/** Reference a theme-aware CSS custom property: `cssVar("bg-base")`. */
export const cssVar = (name: string) => `var(--${name})`;

export const px = (value: number) => `${value}px`;
